import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loginSchema, type LoginFormValues } from '../lib/validations';
import { loginUser } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { queryClient } from '../lib/queryClient';
import Wordmark from '../components/Brand';
import { LogIn, Mail } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  // CHG-141 REQ-IF-013: 초대 흐름에서 진입 시 어느 이메일로 로그인해야 하는지 안내
  const expectEmail = searchParams.get('expectEmail');
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: expectEmail ? { email: expectEmail } : undefined,
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const result = await loginUser(data);
      queryClient.clear();
      setAuth(result.user, result.tokens.accessToken, result.tokens.refreshToken);
      toast.success(`${result.user.name}님, 환영합니다!`);
      navigate(returnUrl || '/home');
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      if (errData?.error === 'EMAIL_NOT_VERIFIED') {
        toast.error('이메일 인증을 완료해주세요.');
        navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
        return;
      }
      const msg = errData?.error ?? '로그인에 실패했습니다.';
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-mark.svg" alt="MaioS" className="h-12 w-12 mx-auto mb-2" />
          <Wordmark className="text-3xl" />
          <p className="mt-1 text-sm text-gray-500">이슈 트래킹 시스템</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">로그인</h2>

          {/* CHG-141 REQ-IF-013: expectEmail 안내 */}
          {expectEmail && (
            <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 p-3 flex gap-3">
              <Mail className="size-5 text-primary-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-gray-700">
                  초대를 수락하려면 <strong className="text-gray-900">{expectEmail}</strong>으로 로그인해주세요.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                {...register('password')}
              />
              {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <LogIn className="size-4" />
              {isSubmitting ? '로그인 중...' : '로그인'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            계정이 없으신가요?{' '}
            <Link to="/register" className="text-primary-600 font-medium hover:text-primary-700">
              회원가입
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
